/**
 * PURPOSE: Guardas mínimas de compatibilidad para un partido SIN participantes asignados
 *   (FTR-0028 D-31, §7.5).
 * RESPONSIBILITIES: Desde el Centro de Programación un partido puede existir en Hayllis ANTES del
 *   sorteo — "A1 vs A4", con fecha, hora y sede, pero con `Participant1`/`Participant2` en null. Ese
 *   estado ya era alcanzable hoy en fase final; el Centro lo vuelve habitual. Estas funciones son la
 *   única respuesta: un texto seguro en lugar de una excepción o una celda en blanco.
 *
 *   NO ES UN REDISEÑO. Un partido correctamente poblado sale exactamente igual que antes: los
 *   helpers devuelven lo mismo que la expresión que sustituyen. Lo único que cambia es qué ocurre
 *   cuando falta un participante.
 *
 *   EL FALLO QUE CORRIGEN. El patrón repetido en Programación y Resultados es
 *     moodName === 1 ? p?.Institution?.abrev : getParticipantFullName(p) || "Por Definir"
 *   donde `|| "Por Definir"` pertenece SOLO a la rama falsa por precedencia de `?:` frente a `||`.
 *   Con `moodName === 1` (el valor por defecto y el habitual) y un participante ausente la celda
 *   queda vacía. `participantLabel` aplica el respaldo a TODO el condicional, que es lo que el
 *   código pretendía decir.
 * COLLABORATORS: participantDisplayName.js (la regla FTR-0006 de moodName, que no se toca),
 *   Fixture.jsx, MatchDay.jsx, Programacion.jsx, Resultados.jsx, FechaSection.jsx.
 * ROUTE: src/utils/pendingMatchLabel.js
 */
import { getParticipantLabel } from "./participantDisplayName.js";

export const PENDING_TEAM_LABEL = "Por Definir";

const normalize = (value) =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const resolveInstitution = (participant) =>
  participant?.Institution ?? participant?.institution ?? null;

/**
 * Etiqueta de un lado, respetando `moodName` (FTR-0006) y sin dejar nunca la celda vacía.
 * `fallback` permite un texto distinto donde la pantalla ya usaba otro.
 */
export const participantLabel = (participant, moodName, fallback = PENDING_TEAM_LABEL) =>
  normalize(getParticipantLabel(participant, moodName)) ?? fallback;

/** ¿El partido tiene los dos lados asignados? Un BYE, por definición, no. */
export const hasBothParticipants = (match) =>
  Boolean(match?.Participant1 && match?.Participant2);

/**
 * ¿Es un descanso REAL? `flag_bye` es la autoridad (mismo predicado que el backend). Un partido sin
 * participantes NO es un descanso: en fase de grupos antes del sorteo, y en fase final antes de
 * conocerse los clasificados, los dos lados están vacíos y el partido sí se jugará.
 */
export const isByeMatch = (match) => {
  const flag = match?.flag_bye;
  return flag === true || Number(flag) === 1 || String(flag).toLowerCase() === "true";
};

/**
 * Texto del descanso. Antes se derivaba de `Participant1 ? ... : Participant2.Institution.abrev`,
 * que lanza `TypeError` cuando los dos lados son null — exactamente el caso que el Centro crea.
 * Devuelve `null` cuando no hay a quién nombrar, para que quien llama decida qué pintar.
 */
export const byeRestLabel = (match, moodName = 1) => {
  const resting = match?.ParticipantBye ?? match?.Participant1 ?? match?.Participant2 ?? null;
  const label = normalize(getParticipantLabel(resting, moodName))
    ?? normalize(resolveInstitution(resting)?.abrev);
  return label ? `${label} descansa` : null;
};

/**
 * Qué mostrar en el centro de una tarjeta cuando no hay dos participantes:
 *   - descanso real -> "X descansa" (o el texto neutro si no se sabe quién);
 *   - partido pendiente -> el texto pendiente, nunca una excepción.
 */
export const pendingMatchText = (match, moodName = 1) => {
  if (isByeMatch(match)) return byeRestLabel(match, moodName) ?? "Descansa un equipo";
  return `${PENDING_TEAM_LABEL} vs ${PENDING_TEAM_LABEL}`;
};

/**
 * Logo seguro de un lado: cadena vacía cuando no hay institución, para que `<img src>` no reciba
 * "undefinedundefined" (que el navegador pide al servidor y falla).
 */
export const institutionLogoSrc = (participant) => {
  const institution = resolveInstitution(participant);
  const path = normalize(institution?.image_path);
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  return `${normalize(institution?.path_base) ?? ""}${path}`;
};
